import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { I18nService } from './i18n.service';
import { SessionService } from './session.service';
import { closedLabel, formatOpensAt } from '../utils/opens-at';

import { environment } from '../../../environments/environment';
import { ApiEnvelope } from '../models/auth.model';
import { ApiBranch } from '../models/branch-api.model';
import { FulfillmentMode } from '../models/order.model';

export function mapOrderType(t: string): FulfillmentMode | null {
  if (t === 'delivery') return 'delivery';
  if (t === 'pickup' || t === 'pickup') return 'pickup';
  if (t === 'dine_in' || t === 'dinein' || t === 'dine-in') return 'dinein';
  return null;
}

/**
 * Real branches from GET /api/v1/branches (name, address, distance, is_nearest,
 * is_default, server-computed open/closed status, order types). Also tracks the
 * currently selected branch id — the branch the menu + prices are built for.
 * Loaded via APP_INITIALIZER before bootstrap.
 */
@Injectable({ providedIn: 'root' })
export class BranchService {
  private readonly base = environment.apiBase;
  private _branches: ApiBranch[] = [];
  private readonly _currentId = new BehaviorSubject<string | null>(null);
  readonly currentId$ = this._currentId.asObservable();
  /** Fires after every (re)load — e.g. once the location arrives and nearest/distances resolve. */
  private readonly _changed = new Subject<void>();
  readonly changed$ = this._changed.asObservable();

  constructor(private http: HttpClient, private i18n: I18nService, private session: SessionService) {}

  private locating = false;

  load(): Promise<void> {
    this.refreshLocation();
    return this.fetch();
  }

  /** GET /branches with the customer's location in the X-Latitude / X-Longitude headers (when known). */
  private fetch(): Promise<void> {
    const c = this.session.coords;
    let headers = new HttpHeaders();
    if (c) headers = headers.set('X-Latitude', String(c.lat)).set('X-Longitude', String(c.lng));
    return firstValueFrom(this.http.get<ApiEnvelope<ApiBranch[]>>(`${this.base}/api/v1/branches`, { headers }))
      .then((res) => { this._branches = (res.data || []).map(normalizeBranch); this._currentId.next(this.restoreOrDefault()); this._changed.next(); })
      .catch(() => { /* keep empty → screens fall back */ });
  }

  /**
   * Get the device location so /branches carries X-Latitude / X-Longitude (nearest branch +
   * distances). Asks once at app start (browser prompt) unless the customer already blocked it;
   * when a position arrives that's new or has moved (>200 m), branches are re-fetched with the
   * headers. Never holds up app start.
   */
  private refreshLocation(): void {
    if (this.locating || typeof navigator === 'undefined' || !navigator.geolocation) return;
    this.locating = true;
    const locate = () => navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.locating = false;
        const prev = this.session.coords;
        const lat = pos.coords.latitude, lng = pos.coords.longitude;
        this.session.setCoords(lat, lng);
        if (!prev || metersBetween(prev.lat, prev.lng, lat, lng) > 200) this.fetch();
      },
      () => { this.locating = false; }, // blocked / unavailable → branches without distances
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 5 * 60 * 1000 },
    );
    // Permissions API (missing on older Safari): skip only when the customer blocked location.
    const perms = navigator.permissions?.query;
    if (!perms) { locate(); return; }
    navigator.permissions.query({ name: 'geolocation' as PermissionName })
      .then((p) => { if (p.state === 'denied') this.locating = false; else locate(); })
      .catch(() => locate());
  }

  get branches(): ApiBranch[] { return this._branches; }
  /** The branches to advertise (Branches page, "Find a branch") — `show_in_list`; the nearest one first. */
  get listedBranches(): ApiBranch[] {
    const list = this._branches.filter((b) => b.show_in_list !== false);
    const near = this.nearestIdIn(list);
    return near ? [...list.filter((b) => b.id === near), ...list.filter((b) => b.id !== near)] : list;
  }
  /**
   * The branch closest to the customer: the smallest `distance_km` (the server's `is_nearest` can
   * point at a farther branch, which would read wrong next to the distances); without distances,
   * the server's `is_nearest`.
   */
  isNearest(b: ApiBranch): boolean { return !!b && b.id === this.nearestIdIn(this.listedBranchesRaw); }
  private get listedBranchesRaw(): ApiBranch[] { return this._branches.filter((b) => b.show_in_list !== false); }
  private nearestIdIn(list: ApiBranch[]): string | null {
    const withDist = list.filter((b) => b.distance_km != null);
    if (withDist.length) return withDist.reduce((a, b) => ((b.distance_km as number) < (a.distance_km as number) ? b : a)).id;
    return list.find((b) => b.is_nearest)?.id ?? null; // no distances (no location) → server flag, if any
  }
  /** The branches customers can order from — `accepts_online_orders`. */
  get orderableBranches(): ApiBranch[] { return this._branches.filter(isOrderable); }
  isOrderable(b: ApiBranch | undefined): boolean { return !!b && isOrderable(b); }
  get loaded(): boolean { return this._branches.length > 0; }
  byId(id: string | null): ApiBranch | undefined { return id ? this._branches.find((b) => b.id === id) : undefined; }

  get currentId(): string | null { return this._currentId.value; }
  get current(): ApiBranch | undefined { return this.byId(this.currentId); }
  setCurrent(id: string | null): void { this._currentId.next(id); }

  /** On (re)load, keep the branch the customer previously selected (persisted in
   *  the session) so a refresh doesn't snap the menu back to the first branch. */
  private restoreOrDefault(): string | null {
    const saved = this.savedBranchId();
    if (saved && this.orderableBranches.some((b) => b.id === saved)) return saved;
    return this.defaultId();
  }
  private savedBranchId(): string | null {
    try {
      const raw = localStorage.getItem('ff_branch');
      return raw ? (JSON.parse(raw)?.id || null) : null;
    } catch { return null; }
  }

  private defaultId(): string | null {
    const b = this.orderableBranches;
    return (b.find((x) => x.is_default) || b.find((x) => x.is_nearest) || b[0])?.id ?? null;
  }

  /** Order types offered across all branches. */
  get supportedOrderTypes(): FulfillmentMode[] {
    const set = new Set<FulfillmentMode>();
    this.orderableBranches.forEach((b) => b.order_types.forEach((ot) => { const m = mapOrderType(ot.order_type); if (m) set.add(m); }));
    return [...set];
  }

  /** Branches offering a given order type, nearest → farthest. */
  branchesFor(mode: FulfillmentMode): ApiBranch[] {
    const orderable = this.orderableBranches;
    const matches = orderable.filter((b) => b.order_types.some((ot) => mapOrderType(ot.order_type) === mode));
    // Dine-in can be enabled storefront-wide without a per-branch order type
    // (the table QR resolves the branch) — offer every branch in that case.
    const list = matches.length || mode !== 'dinein' ? matches : [...orderable];
    return list.sort((a, b) => (a.distance_km ?? Number.MAX_SAFE_INTEGER) - (b.distance_km ?? Number.MAX_SAFE_INTEGER));
  }

  statusLabel(b: ApiBranch | undefined): string {
    if (!b) return '';
    if (b.status.is_open) return 'Open';
    return closedLabel(b.status.opens_at, this.i18n.lang); // "Closed · Opens tomorrow at 1:00 PM"
  }

  /** A friendly next-opening line ("Opens tomorrow at 1:00 PM") in the current language. */
  opensLabel(opensAt: string | null | undefined): string {
    return formatOpensAt(opensAt, this.i18n.lang);
  }
}

/**
 * The API can omit `status` (null) and `order_types` for a branch that isn't set up
 * for online ordering yet. Fill safe defaults — closed, no order types — so no screen
 * reading `b.status.is_open` or `b.order_types` breaks (one bad branch used to blank
 * every branch card after it).
 */
function normalizeBranch(b: ApiBranch): ApiBranch {
  return {
    ...b,
    name: b.name || '',
    address: b.address || '',
    status: b.status ?? { is_open: false, opens_at: null },
    order_types: b.order_types ?? [],
  };
}

/** A branch takes online orders unless the admin switched it off. */
function isOrderable(b: ApiBranch): boolean { return b.accepts_online_orders !== false; }

/** Great-circle distance in metres. */
function metersBetween(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const r = 6371000, rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad, dLng = (lng2 - lng1) * rad;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.asin(Math.sqrt(a));
}
